import React, { useState, useEffect, useCallback, useRef } from "react";
import InputFieldFloat from "../../inputs/InputFieldFloat";
import UploadLogo from "../../inputs/UploadLogo";
import {
  useAdminGetDesignQuery,
  useAdminUpdateOrganizationMutation,
} from "../../../services/organization/settings";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import ColorPicker from "../../inputs/ColorPicker";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{6})$/;

export default function WebsiteDesign({ submittingController }) {
  const currentOrganization = useSelector(
    (state) => state.auth.currentOrganization
  );

  const [blockPickerColor, setBlockPickerColor] = useState(
    currentOrganization?.renderColor
  );
  const [hexInput, setHexInput] = useState(
    currentOrganization?.renderColor || ""
  );
  const [viewPicker, setViewPicker] = useState(false);
  const [image, setImage] = useState(currentOrganization?.orgLogo);
  const [isImageChanged, setIsImageChanged] = useState(false);

  const { data: organizationDesign } = useAdminGetDesignQuery();
  const [updateOrganization] = useAdminUpdateOrganizationMutation();

  const { t } = useTranslation(["common", "settings"]);

  const closePicker = useCallback(() => setViewPicker(false), []);
  const colorPreviewRef = useRef(null);

  const applyColor = useCallback((nextColor) => {
    setBlockPickerColor(nextColor);
    setHexInput(nextColor);
  }, []);

  useEffect(() => {
    setHexInput(blockPickerColor || "");
  }, [blockPickerColor]);

  const handleHexChange = (event) => {
    const value = event.target.value.trim();
    setHexInput(value);
    if (HEX_COLOR_PATTERN.test(value)) {
      setBlockPickerColor(value);
    }
  };

  const handleHexBlur = () => {
    if (HEX_COLOR_PATTERN.test(hexInput)) {
      setBlockPickerColor(hexInput);
      return;
    }
    setHexInput(blockPickerColor || "");
  };

  const addLogo = (e) => {
    const imageFile = e?.target?.files[0];
    setIsImageChanged(true);
    setImage(imageFile);
  };

  const validationSchema = yup.object({
    name_ar: yup.string().required(t("settings:oragnizationNameARRequired")),
    name_en: yup.string().required(t("settings:oragnizationNameENRequired")),
    domain: yup
      .string()
      .test(
        "includeSpace",
        t("settings:DomainNoSpace"),
        (value) => !/\s/.test(value)
      )
      .test("onlyEnglishAlphabets", t("settings:DomainNoAr"), (value) =>
        /^[a-zA-Z-]+$/.test(value)
      )
      .required(t("settings:DomainRequired")),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name_ar: organizationDesign?.data?.name_ar
        ? organizationDesign?.data?.name_ar
        : "",
      name_en: organizationDesign?.data?.name_en
        ? organizationDesign?.data?.name_en
        : "",
      domain: currentOrganization?.domain ? currentOrganization?.domain : "",
    },

    validationSchema: validationSchema,

    onSubmit: async (form_values) => {
      try {
        let dataForm = new FormData();
        dataForm.append("name_ar", form_values?.name_ar);
        dataForm.append("name_en", form_values?.name_en);
        dataForm.append("domain", form_values?.domain);
        dataForm.append("color", blockPickerColor);
        if (isImageChanged) {
          dataForm.append("image", image);
        }
        const result = await updateOrganization(dataForm);
        toast.success(t("common:successfulSave"));
      } catch (e) {
        toast.error(t("common:failedSaving"));
      }
    },
  });

  useEffect(() => {
    if (submittingController.isSaving) {
      formik.handleSubmit();
      submittingController.setIsSaving(false);
    }
  }, [submittingController.isSaving]);

  return (
    <div className="py-4 setting__tabs-content">
      <p className="font_14 font-color-grey pb-3">
        {" "}
        {t("settings:siteDesignHint")}{" "}
      </p>
      <form
        className="content-form"
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit(e);
        }}
      >
        <div className="row">
          <div className="col-12 col-md-6">
            <div>
              <InputFieldFloat
                label={t("settings:orgNameAr")}
                type="text"
                name="name_ar"
                value={formik.values.name_ar}
                onChange={formik.handleChange}
                error={formik.errors.name_ar}
                touched={formik.touched.name_ar}
              />
            </div>

            <div>
              <InputFieldFloat
                label={t("settings:orgNameEn")}
                type="text"
                name="name_en"
                value={formik.values.name_en}
                onChange={formik.handleChange}
                error={formik.errors.name_en}
                touched={formik.touched.name_en}
              />
            </div>

            <div>
              <InputFieldFloat
                label={t("settings:websiteDomain")}
                type="text"
                name="domain"
                value={formik.values.domain}
                onChange={formik.handleChange}
                error={formik.errors.domain}
                touched={formik.touched.domain}
                marginBottom="5"
              />
              <p
                style={{
                  marginTop:
                    formik.errors.domain && formik.touched.domain ? 21 : 0,
                }}
                className="mb-0 font_12 font-color-grey"
              >
                <i className="fas fa-info-circle me-2" />
                {t("settings:DomainNameHint")}
              </p>
            </div>
          </div>
        </div>

        <div className="admin-settings__logo-section mt-4">
          <UploadLogo image={image} onChange={(e) => addLogo(e)} />
        </div>

        <div className="mt-4 admin-settings__color-section">
          <h1 className="title-page mb-1">{t("settings:mainColor")}</h1>
          <p className="font_14 font-color-grey mb-3 text-start">
            {t("settings:mainColorHint")}
          </p>

          <div className="admin-settings__color-control">
            <button
              type="button"
              ref={colorPreviewRef}
              className="admin-settings__color-preview"
              aria-label={t("settings:changeColor")}
              aria-expanded={viewPicker}
              onClick={() => setViewPicker((open) => !open)}
            >
              <span
                className="admin-settings__color-preview-swatch"
                style={{ backgroundColor: blockPickerColor }}
              />
              <span className="admin-settings__color-preview-meta">
                <span className="admin-settings__color-preview-label">
                  {t("settings:colorCode")}
                </span>
                <span className="admin-settings__color-preview-value">
                  {(blockPickerColor || "").toUpperCase()}
                </span>
              </span>
              <span className="admin-settings__color-preview-action">
                <i className="fas fa-palette fa-fw" aria-hidden="true" />
                {t("settings:changeColor")}
              </span>
            </button>

            <label className="admin-settings__color-hex">
              <span className="admin-settings__color-hex-label">
                {t("settings:colorCode")}
              </span>
              <input
                type="text"
                name="color"
                className="admin-settings__color-hex-input"
                value={hexInput}
                onChange={handleHexChange}
                onBlur={handleHexBlur}
                onFocus={() => setViewPicker(false)}
                spellCheck={false}
                autoComplete="off"
                dir="ltr"
                maxLength={7}
                placeholder="#000000"
              />
            </label>

            <ColorPicker
              blockPickerColor={blockPickerColor}
              setBlockPickerColor={applyColor}
              visible={viewPicker}
              onClose={closePicker}
              ignoreRefs={[colorPreviewRef]}
            />
          </div>
        </div>
      </form>
      <br />
    </div>
  );
}
